// Figure-to-source traceability (v3 §Chunk 4). Given any anchor, resolve what it rolls
// up to and what it is composed of, down to evidence and the audit trail.

import { assuranceOf } from "./hooks";
import { formatHumanCurrency } from "@/design/format";
import { traceApi } from "./api";
import type {
  TraceProjectRow,
  TraceSpendRequestRow,
  TraceEvidenceRow,
  TraceDisbursementRow,
} from "./api";
import type { WorkspaceSeed, AuditEvent, AssuranceLevel } from "./types";

export type TraceTarget =
  | { kind: "spend"; id: string }
  | { kind: "disbursement"; id: string }
  | { kind: "project"; id: string }
  | { kind: "subProject"; id: string }
  | { kind: "milestone"; id: string };

export type TraceNode = {
  label: string;
  sublabel?: string;
  amount?: number;
  assurance?: AssuranceLevel;
  target?: TraceTarget;
};

/** One step in the path from the top of the graph down to the current node. */
export type TraceCrumb = {
  label: string;
  /** Absent for a non-clickable ancestor (e.g. the Programme). */
  target?: TraceTarget;
};

export type TraceResult = {
  title: string;
  kindLabel: string;
  amount?: number;
  meta: string[];
  up: TraceNode[];
  down: TraceNode[];
  /**
   * Lateral relations that are neither ancestry nor composition — chiefly the
   * justification edge between a spend ask and the disbursement it backs. Reads
   * the same from both sides, so it is never mistaken for the structural chain.
   */
  related?: TraceNode[];
  /**
   * Count of evidence documents attached to spend asks beneath this node. Lets the
   * drawer always offer an Evidence section that points deeper when the documents
   * live below this level rather than on it.
   */
  evidenceBelow?: number;
  evidence: { id: string; label: string; type: string; fileUrl?: string; filename?: string; note?: string; geo?: { lat: number; lng: number }; capturedAt?: string }[];
  audit: AuditEvent[];
  /** Other nodes at the same level under the same parent, for the sibling shelf. */
  siblings?: TraceNode[];
};

const ASSURANCE_LABEL: Record<AssuranceLevel, string> = {
  self_reported: "Self-reported",
  document_backed: "Document-backed",
  statement_matched: "Statement-matched",
  verified: "Independently verified",
};

const AUDIT_CAP = 10;

/**
 * Activity for a node, read as a chronological timeline. Prefers events tagged with the
 * node's real id (refId); falls back to the brittle description substring match where no
 * event is tagged, so older seed data still surfaces. Newest first, capped to keep it scannable.
 */
function auditFor(
  seed: WorkspaceSeed,
  opts: { refId?: string; substring?: string },
): AuditEvent[] {
  const byRef = opts.refId
    ? seed.auditEvents.filter((e) => e.refId === opts.refId)
    : [];
  const matched = byRef.length
    ? byRef
    : opts.substring
      ? seed.auditEvents.filter((e) => e.description.includes(opts.substring!))
      : [];
  return [...matched]
    .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))
    .slice(0, AUDIT_CAP);
}

/** Count evidence documents attached to a set of spend asks (evidence links to asks via linkedAskId). */
function evidenceCountForAsks(seed: WorkspaceSeed, askIds: string[]): number {
  if (!askIds.length) return 0;
  const ids = new Set(askIds);
  return (seed.evidence ?? []).filter((e) => ids.has(e.linkedAskId)).length;
}

/**
 * The ordered path from the TOP of the graph down to and including the target — one crumb
 * per real parent link, so the breadcrumb tells you exactly what you are looking at. A crumb
 * with no target is non-clickable (e.g. the Programme). The lateral disbursement justification
 * is never part of an ancestry; it is surfaced as a relation by resolveTrace instead.
 */
export function resolveAncestry(seed: WorkspaceSeed, target: TraceTarget): TraceCrumb[] {
  const projName = (id?: string) => seed.projects.find((p) => p.id === id)?.name ?? "";
  const subName = (id?: string) => seed.subProjects.find((s) => s.id === id)?.name ?? "";
  const msName = (id?: string) => seed.milestones.find((m) => m.id === id)?.name ?? "";
  const programme: TraceCrumb = { label: seed.programme.name };

  if (target.kind === "spend") {
    const r = seed.spendRequests.find((x) => x.id === target.id);
    if (!r) return [programme];
    const crumbs: TraceCrumb[] = [programme];
    crumbs.push({ label: projName(r.projectId), target: { kind: "project", id: r.projectId } });
    if (r.subProjectId) crumbs.push({ label: subName(r.subProjectId), target: { kind: "subProject", id: r.subProjectId } });
    crumbs.push({ label: r.title, target });
    return crumbs;
  }

  if (target.kind === "subProject") {
    const s = seed.subProjects.find((x) => x.id === target.id);
    if (!s) return [programme];
    return [
      programme,
      { label: projName(s.projectId), target: { kind: "project", id: s.projectId } },
      { label: s.name, target },
    ];
  }

  if (target.kind === "project") {
    const p = seed.projects.find((x) => x.id === target.id);
    if (!p) return [programme];
    return [programme, { label: p.name, target }];
  }

  if (target.kind === "milestone") {
    const m = seed.milestones.find((x) => x.id === target.id);
    if (!m) return [programme];
    return [
      programme,
      { label: projName(m.projectId), target: { kind: "project", id: m.projectId } },
      { label: m.name, target },
    ];
  }

  // disbursement — structural parent is its milestone (and the milestone's project)
  const d = seed.disbursements.find((x) => x.id === target.id);
  if (!d) return [programme];
  const ms = seed.milestones.find((m) => m.id === d.milestoneId);
  const crumbs: TraceCrumb[] = [programme];
  if (ms?.projectId) crumbs.push({ label: projName(ms.projectId), target: { kind: "project", id: ms.projectId } });
  if (ms) crumbs.push({ label: ms.name, target: { kind: "milestone", id: ms.id } });
  crumbs.push({ label: `${ms?.name ?? "Milestone"} disbursement`, target });
  return crumbs;
}

export function resolveTrace(seed: WorkspaceSeed, target: TraceTarget): TraceResult | null {
  const projName = (id?: string) => seed.projects.find((p) => p.id === id)?.name ?? "";
  const subName = (id?: string) => seed.subProjects.find((s) => s.id === id)?.name ?? "";
  const msName = (id?: string) => seed.milestones.find((m) => m.id === id)?.name ?? "";

  if (target.kind === "spend") {
    const r = seed.spendRequests.find((x) => x.id === target.id);
    if (!r) return null;
    const level = assuranceOf(r, seed.evidence ?? [], seed.spendItems);
    const items = seed.spendItems.filter((i) => i.spendRequestId === r.id);
    const evidence = (seed.evidence ?? []).filter((e) => e.linkedAskId === r.id);
    const disb = seed.disbursements.find((d) => (d.basisAskIds ?? []).includes(r.id));
    const up: TraceNode[] = [];
    if (r.subProjectId) up.push({ label: subName(r.subProjectId), sublabel: "Sub-project", target: { kind: "subProject", id: r.subProjectId } });
    up.push({ label: projName(r.projectId), sublabel: "Project", target: { kind: "project", id: r.projectId } });
    if (r.milestoneId) up.push({ label: msName(r.milestoneId), sublabel: "Milestone", target: { kind: "milestone", id: r.milestoneId } });
    // The disbursement is a lateral justification, not an ancestor — surface it as a relation.
    const related: TraceNode[] = [];
    if (disb) related.push({ label: `${msName(disb.milestoneId)} disbursement`, sublabel: "Justifies this disbursement", amount: disb.amount, target: { kind: "disbursement", id: disb.id } });
    const down: TraceNode[] = items.map((i) => ({ label: i.description, sublabel: i.supplier, amount: i.amount }));
    const meta = [
      ASSURANCE_LABEL[level],
      r.path === "recorded" ? "Recorded within authority" : r.path === "pre_auth" ? "Pre-authorised" : "",
      r.statementMatchRef ? `Statement ${r.statementMatchRef}` : "",
      r.gapExplanation ? `Gap explained: ${r.gapExplanation.body}` : "",
    ].filter(Boolean);
    return {
      title: r.title,
      kindLabel: "Spend",
      amount: r.amount,
      meta,
      up,
      down,
      related: related.length ? related : undefined,
      evidence: evidence.map((e) => ({ id: e.id, label: e.filename ?? e.note ?? e.type, type: e.type })),
      audit: auditFor(seed, { refId: r.id, substring: r.title }),
    };
  }

  if (target.kind === "disbursement") {
    const d = seed.disbursements.find((x) => x.id === target.id);
    if (!d) return null;
    const ms = seed.milestones.find((m) => m.id === d.milestoneId);
    const basis = (d.basisAskIds ?? []).map((id) => seed.spendRequests.find((r) => r.id === id)).filter(Boolean);
    const up: TraceNode[] = [];
    if (ms) up.push({ label: ms.name, sublabel: "Milestone", target: { kind: "milestone", id: ms.id } });
    if (ms?.projectId) up.push({ label: projName(ms.projectId), sublabel: "Project", target: { kind: "project", id: ms.projectId } });
    up.push({ label: seed.programme.name, sublabel: "Programme" });
    const down: TraceNode[] = basis.map((r) => ({
      label: r!.title,
      sublabel: r!.supplier,
      amount: r!.amount,
      assurance: assuranceOf(r!, seed.evidence ?? [], seed.spendItems),
      target: { kind: "spend", id: r!.id },
    }));
    return {
      title: `${ms?.name ?? "Milestone"} disbursement`,
      kindLabel: "Disbursement",
      amount: d.amount,
      meta: [`Released against ${basis.length} confirmed ask${basis.length === 1 ? "" : "s"}`, d.state.replace(/_/g, " ")],
      up,
      down,
      evidenceBelow: evidenceCountForAsks(seed, (d.basisAskIds ?? []) as string[]),
      evidence: [],
      audit: auditFor(seed, { refId: d.id, substring: ms?.name }),
    };
  }

  if (target.kind === "project") {
    const p = seed.projects.find((x) => x.id === target.id);
    if (!p) return null;
    const subs = seed.subProjects.filter((s) => s.projectId === p.id);
    const askIds = seed.spendRequests.filter((r) => r.projectId === p.id && r.state !== "rejected").map((r) => r.id);
    return {
      title: p.name,
      kindLabel: "Project",
      amount: p.spent,
      meta: [`Budgeted ${formatHumanCurrency(p.budgeted)}`, `Spent ${formatHumanCurrency(p.spent)}`, `Evidenced ${formatHumanCurrency(p.evidenced)}`],
      up: [{ label: seed.programme.name, sublabel: "Programme" }],
      down: subs.map((s) => ({ label: s.name, sublabel: "Sub-project", amount: s.spent, target: { kind: "subProject", id: s.id } })),
      evidenceBelow: evidenceCountForAsks(seed, askIds),
      evidence: [],
      audit: seed.auditEvents.filter((e) => e.scope.projectId === p.id).sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1)).slice(0, AUDIT_CAP),
    };
  }

  if (target.kind === "subProject") {
    const s = seed.subProjects.find((x) => x.id === target.id);
    if (!s) return null;
    const asks = seed.spendRequests.filter((r) => r.subProjectId === s.id && r.state !== "rejected");
    return {
      title: s.name,
      kindLabel: "Sub-project",
      amount: s.spent,
      meta: [`Budgeted ${formatHumanCurrency(s.budgeted)}`, `Spent ${formatHumanCurrency(s.spent)}`],
      up: [
        { label: projName(s.projectId), sublabel: "Project", target: { kind: "project", id: s.projectId } },
        { label: seed.programme.name, sublabel: "Programme" },
      ],
      down: asks.map((r) => ({ label: r.title, sublabel: r.supplier, amount: r.amount, assurance: assuranceOf(r, seed.evidence ?? [], seed.spendItems), target: { kind: "spend", id: r.id } })),
      evidenceBelow: evidenceCountForAsks(seed, asks.map((r) => r.id)),
      evidence: [],
      audit: seed.auditEvents.filter((e) => e.scope.subProjectId === s.id).sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1)).slice(0, AUDIT_CAP),
    };
  }

  // milestone
  const m = seed.milestones.find((x) => x.id === target.id);
  if (!m) return null;
  const disb = seed.disbursements.find((d) => d.milestoneId === m.id);
  const asks = seed.spendRequests.filter((r) => r.milestoneId === m.id && r.state !== "rejected");
  const down: TraceNode[] = [];
  for (const r of asks) down.push({ label: r.title, sublabel: r.supplier, amount: r.amount, assurance: assuranceOf(r, seed.evidence ?? [], seed.spendItems), target: { kind: "spend", id: r.id } });
  // The disbursement released against this milestone is a lateral justification, not composition.
  const related: TraceNode[] = [];
  if (disb) related.push({ label: `${m.name} disbursement`, sublabel: "Disbursement released against this milestone", amount: disb.amount, target: { kind: "disbursement", id: disb.id } });
  return {
    title: m.name,
    kindLabel: "Milestone",
    meta: [m.status.replace(/_/g, " "), ...(m.evidenceRequirements?.length ? [`Gate evidence: ${m.evidenceRequirements.join(", ")}`] : [])],
    up: [
      { label: projName(m.projectId), sublabel: "Project", target: { kind: "project", id: m.projectId } },
      { label: seed.programme.name, sublabel: "Programme" },
    ],
    down,
    related: related.length ? related : undefined,
    evidenceBelow: evidenceCountForAsks(seed, asks.map((r) => r.id)),
    evidence: [],
    audit: auditFor(seed, { refId: m.id, substring: m.name }),
  };
}

// ---------------------------------------------------------------------------
// Async resolution — the real-API path (server/routers/trace.js), used when
// not in demo mode. Produces the exact same TraceResult/TraceCrumb shapes as
// resolveTrace/resolveAncestry above, so every rendering component is
// agnostic to which path filled them in. A project row is the "sub_project"
// trace kind whenever it has a parent — projectKindOf is the one place that
// distinction gets made for API data (the seed already keeps `projects` and
// `subProjects` as separate arrays, so the sync path never needs this).
// ---------------------------------------------------------------------------

function projectKindOf(row: Pick<TraceProjectRow, "parentprojectid">): "project" | "subProject" {
  return row.parentprojectid != null ? "subProject" : "project";
}

function num(v: number | string | null | undefined): number {
  if (v == null) return 0;
  return typeof v === "string" ? Number(v) || 0 : v;
}

/**
 * Approximation of assuranceOf (domain/hooks.ts) for the real-API path.
 * The full demo model also weighs statement-matching and spend-item-level
 * evidence, which aren't exposed by the trace endpoints today — this reads
 * the closest real signals (a human verifier, or an attached evidence file)
 * rather than reimplementing every input the seed-based model has access to.
 */
function apiAssuranceOf(spendRequest: TraceSpendRequestRow, evidence: TraceEvidenceRow[]): AssuranceLevel {
  if (spendRequest.verifiedbyuserid) return "verified";
  return evidence.some((e) => e.fileurl || e.notes) ? "document_backed" : "self_reported";
}

function evidenceRowToNode(e: TraceEvidenceRow): TraceResult["evidence"][number] {
  return {
    id: String(e.id),
    label: e.filename ?? e.notes ?? e.filetype ?? "Evidence",
    type: e.filetype ?? "document",
    fileUrl: e.fileurl ?? undefined,
    filename: e.filename ?? undefined,
    note: e.notes ?? undefined,
    geo: e.geolatitude != null && e.geolongitude != null ? { lat: num(e.geolatitude), lng: num(e.geolongitude) } : undefined,
    capturedAt: e.geocapturedat ?? undefined,
  };
}

/** Other project rows sharing the same parent (or top-level programme siblings) as TraceNodes. */
async function projectSiblingNodes(projectId: string): Promise<TraceNode[]> {
  const { siblings } = await traceApi.getProjectSiblings(projectId);
  return siblings.map((s) => ({
    label: s.title || s.name,
    sublabel: projectKindOf(s) === "subProject" ? "Sub-project" : "Project",
    amount: num(s.spent),
    target: { kind: projectKindOf(s), id: String(s.id) },
  }));
}

/** Other spend requests sharing the same immediate parent as TraceNodes. */
async function spendSiblingNodes(spendRequestId: string): Promise<TraceNode[]> {
  const { siblings } = await traceApi.getSpendRequestSiblings(spendRequestId);
  return siblings.map((r) => ({
    label: r.title,
    sublabel: r.targetsupplier ?? undefined,
    amount: num(r.amount),
    target: { kind: "spend", id: String(r.id) },
  }));
}

export async function resolveAncestryAsync(target: TraceTarget): Promise<TraceCrumb[]> {
  if (target.kind === "spend") {
    const { spendRequest, project, subProject } = await traceApi.getSpendRequest(target.id);
    const crumbs: TraceCrumb[] = [];
    if (project) crumbs.push({ label: project.title || project.name, target: { kind: "project", id: String(project.id) } });
    if (subProject) crumbs.push({ label: subProject.title || subProject.name, target: { kind: "subProject", id: String(subProject.id) } });
    crumbs.push({ label: spendRequest.title, target });
    return crumbs.length ? crumbs : [{ label: "…", target }];
  }

  if (target.kind === "project" || target.kind === "subProject") {
    const { project, parentProject } = await traceApi.getProject(target.id);
    const crumbs: TraceCrumb[] = [];
    if (parentProject) crumbs.push({ label: parentProject.title || parentProject.name, target: { kind: "project", id: String(parentProject.id) } });
    crumbs.push({ label: project.title || project.name, target });
    return crumbs;
  }

  if (target.kind === "milestone") {
    const { milestone, project } = await traceApi.getMilestone(target.id);
    const crumbs: TraceCrumb[] = [];
    if (project) crumbs.push({ label: project.title || project.name, target: { kind: "project", id: String(project.id) } });
    crumbs.push({ label: milestone.name, target });
    return crumbs;
  }

  // disbursement
  const { disbursement, milestone, project } = await traceApi.getDisbursement(target.id);
  const crumbs: TraceCrumb[] = [];
  if (project) crumbs.push({ label: project.title || project.name, target: { kind: "project", id: String(project.id) } });
  if (milestone) crumbs.push({ label: milestone.name, target: { kind: "milestone", id: String(milestone.id) } });
  crumbs.push({ label: `${milestone?.name ?? "Milestone"} disbursement`, target });
  return crumbs;
}

export async function resolveTraceAsync(target: TraceTarget): Promise<TraceResult | null> {
  try {
    if (target.kind === "spend") {
      const [{ spendRequest, project, subProject, milestone, disbursement }, { evidence }] = await Promise.all([
        traceApi.getSpendRequest(target.id),
        traceApi.getEvidence(target.id),
      ]);
      const level = apiAssuranceOf(spendRequest, evidence);
      const up: TraceNode[] = [];
      if (subProject) up.push({ label: subProject.title || subProject.name, sublabel: "Sub-project", target: { kind: "subProject", id: String(subProject.id) } });
      if (project) up.push({ label: project.title || project.name, sublabel: "Project", target: { kind: "project", id: String(project.id) } });
      if (milestone) up.push({ label: milestone.name, sublabel: "Milestone", target: { kind: "milestone", id: String(milestone.id) } });
      const related: TraceNode[] = [];
      if (disbursement) related.push({ label: `${milestone?.name ?? "Milestone"} disbursement`, sublabel: "Justifies this disbursement", amount: num(disbursement.amount), target: { kind: "disbursement", id: String(disbursement.id) } });
      return {
        title: spendRequest.title,
        kindLabel: "Spend",
        amount: num(spendRequest.amount),
        meta: [ASSURANCE_LABEL[level]].filter(Boolean),
        up,
        down: [],
        related: related.length ? related : undefined,
        evidence: evidence.map(evidenceRowToNode),
        audit: [],
        siblings: await spendSiblingNodes(target.id),
      };
    }

    if (target.kind === "disbursement") {
      const { disbursement, milestone, project, basisAsks } = await traceApi.getDisbursement(target.id);
      const up: TraceNode[] = [];
      if (milestone) up.push({ label: milestone.name, sublabel: "Milestone", target: { kind: "milestone", id: String(milestone.id) } });
      if (project) up.push({ label: project.title || project.name, sublabel: "Project", target: { kind: "project", id: String(project.id) } });
      const down: TraceNode[] = basisAsks.map((r) => ({
        label: r.title,
        sublabel: r.targetsupplier ?? undefined,
        amount: num(r.amount),
        target: { kind: "spend", id: String(r.id) },
      }));
      return {
        title: `${milestone?.name ?? "Milestone"} disbursement`,
        kindLabel: "Disbursement",
        amount: num(disbursement.amount),
        meta: [`Released against ${basisAsks.length} confirmed ask${basisAsks.length === 1 ? "" : "s"}`, disbursement.state.replace(/_/g, " ")],
        up,
        down,
        evidence: [],
        audit: [],
      };
    }

    if (target.kind === "project" || target.kind === "subProject") {
      const { project, childProjects, spendRequests } = await traceApi.getProject(target.id);
      const down: TraceNode[] = childProjects.length
        ? childProjects.map((c) => ({ label: c.title || c.name, sublabel: "Sub-project", amount: num(c.spent), target: { kind: "subProject" as const, id: String(c.id) } }))
        : spendRequests.map((r) => ({ label: r.title, sublabel: r.targetsupplier ?? undefined, amount: num(r.amount), target: { kind: "spend" as const, id: String(r.id) } }));
      return {
        title: project.title || project.name,
        kindLabel: target.kind === "subProject" ? "Sub-project" : "Project",
        amount: num(project.spent),
        meta: [`Budgeted ${formatHumanCurrency(num(project.budgeted))}`, `Spent ${formatHumanCurrency(num(project.spent))}`],
        up: [],
        down,
        evidence: [],
        audit: [],
        siblings: await projectSiblingNodes(target.id),
      };
    }

    // milestone
    const { milestone, project, spendRequests, disbursement } = await traceApi.getMilestone(target.id);
    const down: TraceNode[] = spendRequests.map((r) => ({ label: r.title, sublabel: r.targetsupplier ?? undefined, amount: num(r.amount), target: { kind: "spend", id: String(r.id) } }));
    const related: TraceNode[] = [];
    if (disbursement) related.push({ label: `${milestone.name} disbursement`, sublabel: "Disbursement released against this milestone", amount: num(disbursement.amount), target: { kind: "disbursement", id: String(disbursement.id) } });
    return {
      title: milestone.name,
      kindLabel: "Milestone",
      meta: [milestone.status.replace(/_/g, " "), ...(milestone.evidencerequirements?.length ? [`Gate evidence: ${milestone.evidencerequirements.join(", ")}`] : [])],
      up: project ? [{ label: project.title || project.name, sublabel: "Project", target: { kind: "project", id: String(project.id) } }] : [],
      down,
      related: related.length ? related : undefined,
      evidence: [],
      audit: [],
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// "Where next" hop cards (Trace Source spec §3.6) — derived from the up/
// related arrays resolveTrace/resolveTraceAsync already compute, rather than
// a static per-entity-type table, so a hop card can never point somewhere
// the up/related arrays don't actually support.
// ---------------------------------------------------------------------------

export type NextHop = { arrow: string; verb: string; label: string; hint?: string; target: TraceTarget };

/** The verb for walking UP from `fromKind` to a node of `toKind`. */
function upVerb(fromKind: TraceTarget["kind"], toKind: TraceTarget["kind"]): string {
  if (toKind === "subProject") return "Sits inside";
  if (toKind === "project") return fromKind === "milestone" || fromKind === "subProject" ? "Sits inside" : "Rolls up to";
  if (toKind === "milestone") return "Rolls up to";
  return "Rolls up to";
}

export function nextHopCards(
  target: TraceTarget,
  result: TraceResult,
  rootTarget: TraceTarget,
  rootLabel: string,
): NextHop[] {
  const cards: NextHop[] = [];

  for (const n of result.up) {
    if (!n.target) continue;
    cards.push({ arrow: "↑", verb: upVerb(target.kind, n.target.kind), label: n.label, hint: n.sublabel, target: n.target });
  }

  for (const n of (result.related ?? [])) {
    if (!n.target) continue;
    const verb = n.target.kind === "disbursement" ? "Justifies" : "Related to";
    cards.push({ arrow: "↑", verb, label: n.label, hint: n.sublabel, target: n.target });
  }

  const isRoot = target.kind === rootTarget.kind && target.id === rootTarget.id;
  if (!isRoot && rootTarget.kind === "disbursement" && !cards.some((c) => c.target.kind === "disbursement")) {
    cards.push({ arrow: "◀", verb: "Return to root", label: rootLabel, hint: "The tranche you're approving", target: rootTarget });
  }

  return cards;
}
